import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Card, Button, Table, Badge, Modal, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useToast } from 'context/providers/ToastProvider';

// Un botón de copiar sencillo y profesional (reemplaza a CopyButton con imágenes)
const CleanCopyButton = ({ text }) => {
    const { showSuccessToast, showErrorToast } = useToast();
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            showSuccessToast('Copiado al portapapeles');
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            showErrorToast('Error al copiar');
        }
    };

    return (
        <Button
            variant="link"
            size="sm"
            className="p-0 text-secondary ms-2"
            onClick={handleCopy}
            title="Copiar"
        >
            <FontAwesomeIcon icon={copied ? "check" : "copy"} className={copied ? "text-success" : ""} />
        </Button>
    );
};

CleanCopyButton.propTypes = {
    text: PropTypes.string.isRequired
};

/**
 * Modal para ver los detalles de un usuario (Contraseñas, fechas, etc.)
 */
const UserDetailsModal = ({ show, onHide, user, resolveRoleName, formatDate }) => {
    const [showPassword, setShowPassword] = useState(false);
    console.log(resolveRoleName)

    if (!user) return null;

    return (
        <Modal show={show} onHide={() => { onHide(); setShowPassword(false); }} centered>
            <Modal.Header closeButton className="border-0 pb-1">
                <Modal.Title className="fw-semibold text-dark" style={{ fontSize: '1rem' }}>
                    <FontAwesomeIcon icon="user-lock" className="me-2 text-primary" />
                    Detalles del Usuario
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-4 pb-4 pt-3">
                <Row className="mb-3 align-items-center">
                    <Col xs={5} sm={4} className="text-muted" style={{ fontSize: '0.85rem' }}>Correo Electrónico</Col>
                    <Col xs={7} sm={8} className="d-flex align-items-center">
                        <span className="text-dark fw-medium" style={{ fontSize: '0.85rem' }}>{user.vEmail || '—'}</span>
                        {user.vEmail && <CleanCopyButton text={user.vEmail} />}
                    </Col>
                </Row>

                <Row className="mb-3 align-items-center">
                    <Col xs={5} sm={4} className="text-muted" style={{ fontSize: '0.85rem' }}>Usuario</Col>
                    <Col xs={7} sm={8} className="d-flex align-items-center">
                        <span className="text-dark fw-medium" style={{ fontSize: '0.85rem' }}>{user.vUsername || user.vUUIDPosUser || user.idPosUser || '—'}</span>
                        <CleanCopyButton text={user.vUsername || user.vUUIDPosUser || user.idPosUser || ''} />
                    </Col>
                </Row>

                {user.vPassword && (
                    <Row className="mb-4 align-items-center">
                        <Col xs={5} sm={4} className="text-muted" style={{ fontSize: '0.85rem' }}>Contraseña</Col>
                        <Col xs={7} sm={8} className="d-flex align-items-center">
                            <span className="text-dark fw-medium me-2" style={{ fontSize: '0.85rem', letterSpacing: showPassword ? 'normal' : '2px' }}>
                                {showPassword ? user.vPassword : '••••••••'}
                            </span>
                            <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-secondary me-2"
                                onClick={() => setShowPassword(!showPassword)}
                                title={showPassword ? "Ocultar" : "Mostrar"}
                            >
                                <FontAwesomeIcon icon={showPassword ? "eye-slash" : "eye"} />
                            </Button>
                            <CleanCopyButton text={user.vPassword} />
                        </Col>
                    </Row>
                )}

                <div className="d-flex justify-content-between border-top pt-3">
                    <div>
                        <span className="text-muted me-1" style={{ fontSize: '0.8rem' }}>Registro:</span>
                        <span className="text-dark fw-medium" style={{ fontSize: '0.8rem' }}>{formatDate(user.dCreatedDate || user.dStartDate)}</span>
                    </div>
                    {!user.bIsSuperAdmin && (
                        <div className="text-end">
                            <span className="text-muted me-1" style={{ fontSize: '0.8rem' }}>Vencimiento:</span>
                            <span className="text-dark fw-medium" style={{ fontSize: '0.8rem' }}>{formatDate(user.dFinishDate || user.dExpirationDate || user.dUpdatedDate)}</span>
                        </div>
                    )}
                </div>
            </Modal.Body>
        </Modal>
    );
};

UserDetailsModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    user: PropTypes.object,
    resolveRoleName: PropTypes.func.isRequired,
    formatDate: PropTypes.func.isRequired
};

/**
 * UsuariosAkasiaPOS
 * Diseño Limpio, Sencillo y Profesional
 */
const UsuariosAkasiaPOS = ({ usuarios, loading, licencias, onNuevoUsuario, onEditarUsuario, onCrearSuperAdmin }) => {
    const [selectedUser, setSelectedUser] = useState(null);

    const resolveRoleName = (usuario) => usuario.vRolName || usuario.vNombreRol || usuario.vRoleName || (usuario.bIsSuperAdmin ? 'Super Admin' : 'Administrador');

    const formatDate = (d) => {
        if (!d) return '—';
        try {
            const dt = new Date(d);
            if (Number.isNaN(dt.getTime())) return d;
            const day = dt.getDate().toString().padStart(2, '0');
            const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
            return `${day} ${months[dt.getMonth()]} ${dt.getFullYear()}`;
        } catch {
            return d;
        }
    };

    const getStatusBadge = (isSuperAdmin, expirationDate) => {
        if (isSuperAdmin) return { label: 'Activo', bg: 'success' };
        if (!expirationDate) return { label: 'Activo', bg: 'success' };

        try {
            const diffDays = Math.ceil((new Date(expirationDate) - new Date()) / (1000 * 60 * 60 * 24));
            if (diffDays < 0) return { label: 'Vencido', bg: 'danger' };
            if (diffDays <= 7) return { label: 'Por Vencer', bg: 'warning', text: 'dark' };
            return { label: 'Activo', bg: 'success' };
        } catch {
            return { label: 'Activo', bg: 'success' };
        }
    };

    return (
        <Card className="mb-3 border-0 shadow-sm">
            <Card.Header className="bg-light d-flex justify-content-between align-items-center py-3">
                <h6 className="mb-0 fw-bold">
                    <FontAwesomeIcon icon="users" className="me-2 text-primary" />
                    Usuarios Akasia POS
                </h6>
                <div className="d-flex gap-2">
                    {usuarios.length === 0 && licencias?.bIsActive && (
                        <Button onClick={onCrearSuperAdmin} variant="primary" size="sm">
                            <FontAwesomeIcon icon="shield-alt" className="me-1" />
                            Crear Super Admin
                        </Button>
                    )}
                    {usuarios.length > 0 && licencias?.bIsActive && !licencias?.bIsPeriodoPrueba && (
                        <Button onClick={onNuevoUsuario} variant="primary" size="sm">
                            <FontAwesomeIcon icon="plus" className="me-1" />
                            Nuevo Usuario
                        </Button>
                    )}
                </div>
            </Card.Header>

            <Card.Body className="p-0">
                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                        <span className="ms-2 text-muted small">Cargando usuarios...</span>
                    </div>
                ) : usuarios.length === 0 ? (
                    <div className="text-center py-5">
                        <p className="text-muted mb-0">No hay usuarios registrados en esta cuenta.</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <Table hover className="mb-0 align-middle">
                            <thead className="bg-200 text-900">
                                <tr>
                                    <th className="border-0">Correo / Usuario</th>
                                    <th className="border-0">Rol</th>
                                    <th className="border-0">Estado</th>
                                    <th className="border-0 text-end pe-4">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {usuarios.map(u => {
                                    const status = getStatusBadge(u.bIsSuperAdmin, u.dFinishDate || u.dExpirationDate || u.dUpdatedDate);
                                    return (
                                        <tr key={u.idPosUser || u.idUsuarioAkasiaPos || u.idUsuarioNubeAkasia}>
                                            <td>
                                                <div 
                                                    className="fw-semibold text-primary" 
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => setSelectedUser(u)}
                                                    title="Haz clic para ver detalles"
                                                >
                                                    {u.vEmail || u.vUsername || '—'}
                                                </div>
                                            </td>
                                            <td>
                                                <Badge bg={u.bIsSuperAdmin ? "primary" : "secondary"} className="fw-normal">
                                                    {resolveRoleName(u)}
                                                </Badge>
                                            </td>
                                            <td>
                                                <Badge bg={status.bg} text={status.text || "light"} className="fw-normal">
                                                    {status.label}
                                                </Badge>
                                            </td>
                                            <td className="text-end pe-4">
                                                <Dropdown align="end">
                                                    <Dropdown.Toggle variant="light" size="sm" className="border-0 shadow-none">
                                                        <FontAwesomeIcon icon="ellipsis-v" />
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu className="shadow-sm border-0">
                                                        <Dropdown.Item onClick={() => setSelectedUser(u)}>
                                                            <FontAwesomeIcon icon="eye" className="me-2 text-secondary" /> Ver detalles
                                                        </Dropdown.Item>
                                                        <Dropdown.Item onClick={() => onEditarUsuario(u)}>
                                                            <FontAwesomeIcon icon="edit" className="me-2 text-primary" /> Editar
                                                        </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Card.Body>

            <UserDetailsModal
                show={!!selectedUser}
                onHide={() => setSelectedUser(null)}
                user={selectedUser}
                resolveRoleName={resolveRoleName}
                formatDate={formatDate}
            />
        </Card>
    );
};

UsuariosAkasiaPOS.propTypes = {
    usuarios: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired,
    licencias: PropTypes.object,
    onNuevoUsuario: PropTypes.func.isRequired,
    onEditarUsuario: PropTypes.func.isRequired,
    onCrearSuperAdmin: PropTypes.func.isRequired
};

export default UsuariosAkasiaPOS;
